/**
 * useCompanyList hook - manages company list data, filters, pagination, sorting
 * Same pattern as useInviteeList.ts
 * Session key: 'companylist_filter' (matches Angular)
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { getCompanyList } from '../services/companyService'
import {
    COMPANY_LIST_DEBOUNCE_MS,
    COMPANY_LIST_DEBOUNCED_FILTER_KEYS,
    COMPANY_LIST_FAVORITE_DEEP_LINK_ROLES,
    COMPANY_LIST_SESSION_STORAGE_KEY,
} from '../constants'
import type { Company, CompanyFilters, CompanyListResponse } from '../types/company'
import { useUserRole } from '@/hooks/useUserRole'
import { applyCompanyRoleOrgScope } from '../utils/companyListRbac'

/**
 * API may return org_listed_ids values as strings (JSON). Strict `=== 1` in the table then fails
 * and Liberty Listed stays wrong after toggling until a full remount.
 */
function normalizeOrgListedIds(
    raw: Record<string, number | string> | undefined | null
): Record<string, number> {
    if (!raw || typeof raw !== 'object') return {}
    const out: Record<string, number> = {}
    for (const [k, v] of Object.entries(raw)) {
        out[k] = Number(v) === 1 ? 1 : 0
    }
    return out
}

function enrichCompany(company: Company): Company {
    const org_listed_ids = normalizeOrgListedIds(company.org_listed_ids)
    return {
        ...company,
        org_listed_ids,
    }
}

/** Pagination + sort reset with role org scope; status defaults to Active. */
function buildClearedFilters(defaultOrgId: number | string | null): CompanyFilters {
    return {
        page: 1,
        perPage: 15,
        sortBy: 'asc',
        orderBy: 'company_name',
        org_id: defaultOrgId ?? '',
        listed: '1',
    }
}

export function useCompanyList() {
    const { isLibertyRole, orgId, roleName } = useUserRole()
    const fetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    /** Angular `processing` — block sort/filter/page while a list request is in flight. */
    const loadingRef = useRef(false)

    const defaultFilters = useMemo<CompanyFilters>(
        () => ({
            page: 1,
            perPage: 15,
            sortBy: 'asc',
            orderBy: 'company_name',
            org_id: isLibertyRole ? 1 : (orgId || ''),
            /** Default Active (Liberty parity + company roles): only Active companies until user picks All / Not Active */
            listed: '1',
            itr_value: '',
        }),
        [isLibertyRole, orgId]
    )

    const [companies, setCompanies] = useState<Company[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [pagination, setPagination] = useState({
        current: 1,
        last: 1,
        total: 0,
    })
    const [currentFilters, setCurrentFilters] = useState<CompanyFilters | null>(null)
    const currentFiltersRef = useRef<CompanyFilters | null>(null)

    const updateCurrentFilters = useCallback((filters: CompanyFilters | null) => {
        currentFiltersRef.current = filters
        setCurrentFilters(filters)
    }, [])

    const loadFiltersFromSession = useCallback((): CompanyFilters | null => {
        try {
            const saved = sessionStorage.getItem(COMPANY_LIST_SESSION_STORAGE_KEY)
            if (!saved) return null
            const parsed = JSON.parse(saved) as CompanyFilters
            if (parsed && typeof parsed === 'object') return parsed
        } catch (e) {
            console.error('Failed to load company filters from session:', e)
        }
        return null
    }, [])

    const saveFiltersToSession = useCallback((filters: CompanyFilters) => {
        try {
            sessionStorage.setItem(COMPANY_LIST_SESSION_STORAGE_KEY, JSON.stringify(filters))
        } catch (e) {
            console.error('Failed to save company filters to session:', e)
        }
    }, [])

    const getInitialFilters = useCallback((): CompanyFilters => {
        let filters = defaultFilters
        try {
            const sessionFilters = loadFiltersFromSession()
            if (sessionFilters != null && Object.keys(sessionFilters).length > 0) {
                const { aln_id: _removedAlnId, ...rest } =
                    sessionFilters as CompanyFilters & {
                        aln_id?: string
                    }
                filters = { ...defaultFilters, ...rest }
            }
        } catch (e) {
            console.error('Error loading company filters:', e)
        }

        // Angular company-list `routeParams()`: `?favorite_companies` deep link
        if (
            (COMPANY_LIST_FAVORITE_DEEP_LINK_ROLES as readonly string[]).includes(roleName) &&
            new URLSearchParams(window.location.search).get('favorite_companies')
        ) {
            filters = { ...filters, favorite_companies: true }
        }

        // Angular `getAccessiblePermission()`: non-Liberty org is locked to role org
        return applyCompanyRoleOrgScope(filters, isLibertyRole, orgId)
    }, [loadFiltersFromSession, defaultFilters, roleName, isLibertyRole, orgId])

    const fetchCompanies = useCallback(async (filters: CompanyFilters, showLoading = true) => {
        if (showLoading) {
            loadingRef.current = true
            setLoading(true)
        }
        setError(null)

        const scopedFilters = applyCompanyRoleOrgScope(filters, isLibertyRole, orgId)

        try {
            const response: CompanyListResponse = await getCompanyList(scopedFilters)
            setCompanies((response.data || []).map((company) => enrichCompany(company)))
            setPagination({
                current: response.current || 1,
                last: response.last || 1,
                total: response.total || 0,
            })
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch companies'
            setError(errorMessage)
            setCompanies([])
        } finally {
            if (showLoading) {
                loadingRef.current = false
                setLoading(false)
            }
        }
    }, [isLibertyRole, orgId])

    const applyFilters = useCallback(
        (newFilters: Partial<CompanyFilters>) => {
            // Angular: Go disabled while `processing`
            if (loadingRef.current) return

            const baseFilters = currentFiltersRef.current || getInitialFilters()
            const isOnlyPageChange = Object.keys(newFilters).length === 1 && 'page' in newFilters
            const pageValue = newFilters.page !== undefined
                ? newFilters.page
                : (isOnlyPageChange ? baseFilters.page : 1)

            const updatedFilters = applyCompanyRoleOrgScope(
                {
                    ...baseFilters,
                    ...newFilters,
                    page: pageValue,
                },
                isLibertyRole,
                orgId,
            )

            updateCurrentFilters(updatedFilters)
            saveFiltersToSession(updatedFilters)

            const changedKeys = Object.keys(newFilters) as (keyof CompanyFilters)[]
            const onlyDebouncedKeys =
                changedKeys.length > 0 &&
                changedKeys.every((k) => (COMPANY_LIST_DEBOUNCED_FILTER_KEYS as readonly string[]).includes(k))
            if (onlyDebouncedKeys) {
                if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current)
                fetchDebounceRef.current = setTimeout(() => {
                    fetchDebounceRef.current = null
                    fetchCompanies(updatedFilters)
                }, COMPANY_LIST_DEBOUNCE_MS)
            } else {
                if (fetchDebounceRef.current) {
                    clearTimeout(fetchDebounceRef.current)
                    fetchDebounceRef.current = null
                }
                fetchCompanies(updatedFilters)
            }
        },
        [getInitialFilters, saveFiltersToSession, fetchCompanies, updateCurrentFilters, isLibertyRole, orgId]
    )

    const resetFilters = useCallback(() => {
        const cleared = buildClearedFilters(defaultFilters.org_id ?? '')
        saveFiltersToSession(cleared)
        updateCurrentFilters(cleared)
        fetchCompanies(cleared)
    }, [saveFiltersToSession, fetchCompanies, updateCurrentFilters, defaultFilters])

    const changePage = useCallback(
        (page: number) => {
            applyFilters({ page })
        },
        [applyFilters]
    )

    const changePerPage = useCallback(
        (perPage: number) => {
            applyFilters({ perPage, page: 1 })
        },
        [applyFilters]
    )

    const sort = useCallback(
        (orderBy: string) => {
            // Angular thead `control-nonclickable` while `processing`
            if (loadingRef.current) return

            const baseFilters = currentFiltersRef.current || getInitialFilters()
            // Angular company-list `sort()`: always toggle asc/desc, then set orderBy
            const newSortBy = baseFilters.sortBy === 'asc' ? 'desc' : 'asc'

            const updatedFilters = applyCompanyRoleOrgScope(
                {
                    ...baseFilters,
                    orderBy,
                    sortBy: newSortBy,
                },
                isLibertyRole,
                orgId,
            )

            updateCurrentFilters(updatedFilters)
            saveFiltersToSession(updatedFilters)
            fetchCompanies(updatedFilters)
        },
        [getInitialFilters, saveFiltersToSession, fetchCompanies, updateCurrentFilters, isLibertyRole, orgId]
    )

    // Initial load
    useEffect(() => {
        const filters = getInitialFilters()
        updateCurrentFilters(filters)
        fetchCompanies(filters)
    }, [])

    // Clear debounce timer on unmount
    useEffect(() => {
        return () => {
            if (fetchDebounceRef.current) {
                clearTimeout(fetchDebounceRef.current)
                fetchDebounceRef.current = null
            }
        }
    }, [])

    const getEffectiveFilters = useCallback((): CompanyFilters => {
        return currentFiltersRef.current || currentFilters || getInitialFilters()
    }, [currentFilters, getInitialFilters])

    const patchCompanyLibertyListed = useCallback((companyId: number, listed: 0 | 1, orgId: number = 1) => {
        setCompanies((prev) =>
            prev.map((c) =>
                c.company_id === companyId
                    ? enrichCompany({
                          ...c,
                          org_listed_ids: {
                              ...normalizeOrgListedIds(c.org_listed_ids),
                              [String(orgId)]: listed,
                          },
                      })
                    : c
            )
        )
    }, [])

    return {
        companies,
        loading,
        error,
        pagination,
        filters: getEffectiveFilters(),
        applyFilters,
        resetFilters,
        changePage,
        changePerPage,
        sort,
        patchCompanyLibertyListed,
        refetch: () => {
            const filters = getEffectiveFilters()
            fetchCompanies(filters)
        },
    }
}
